import {
  BodyType,
  Breed,
  LifeStyle,
} from "libs/cat-calculations/src/lib/cat-calculations.types";
import { Gender } from "@./cat-calculations";
import {
  AddressValue,
  CancellationReason,
  CatLifeStage,
  MealPlanSize,
  Recipe,
} from "@forms/schema";
import { Dayjs } from "dayjs";
import { TokenSuccessResponse } from "@auth/types";

import { IDiscount } from "@/discounts-utils";

export type Lifestyle = Exclude<LifeStyle, "UNKNOWN">;

export type LegacyRequestTypes =
  IPossibleDeliveryDates
  | ICheckoutCat
  | ILitterCheckoutRequest
  | ILitterSignUpPreference;

interface IPossibleDeliveryDate {
  date: Dayjs; // Of the format "2022-07-14"
  price: number;
}

export interface IPossibleDeliveryDates {
  dates: IPossibleDeliveryDate[];
}

export interface ICheckoutCat {
  name?: string;
  gender?: Gender;
}

export type AddOnPlanType = "HEALTH_SCOOP_LITTER" | "PLANET_SCOOP_LITTER";
export type FreshMealPlanType = "FRESH" | "FRESH_TRIAL" | "FRESH_CROSS_SELL";

type TDiscountNibbles = "TWENTY_OFF" | "FIFTY_OFF" | "ONE_POUND" | undefined; // Undefined means no discount

export interface INewLitterCheckoutRequest {
  discountCode?: string;
  numberOfCats?: number;
  deliveryDate?: string;
  quantity?: number;
  cadence?: number;
  catIds?: string[];
}

export interface ILitterCheckoutRequest {
  discountCode?: string;
  numberOfCats?: number;
  deliveryDate?: string;
  quantity?: number;
  cadence?: number;
  cats?: string[] | null;
  formUsed?: string;
  recurringQuantity?: number;
  recurringCadence?: number;
}

export interface INewCustomerLitterCheckoutRequest {
  deliveryDate: string;
  productQuantity: number;
  cadenceWeeks: number;
  email: string;
  customerId: string;
  discountCode?: string;
  catIds: string[];
  formUsed: string;
  addonPlanType: AddOnPlanType;
  googleClientId?: string;
  postcode: string;
  shippingAddress: ShippingAddress;
  billingAddress: BillingAddress;
  fbclid?: string;
}

export interface ICheckoutStatus {
  lastReportedStatus: CheckoutStatus;
}

export type CheckoutStatus = "IN_PROGRESS" | "FAILED" | "SUCCESS" | "TIMED_OUT";
export interface ShippingAddress {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  country?: string;
  phone?: string;
}

export interface BillingAddress {
  firstName: string;
  lastName: string;
  postcode: string;
  address1: string;
  address2?: string;
  city: string;
  country: string;
  phone?: string;
}

export interface ILitterPlanSignUpOption {
  quantity: number; // Number of bags
  cadence: number; // Number of recurring days
  pricing: {
    planQuantityDiscount: number;
    planPreDiscount: number;
    delivery: number;
    planPostDiscount: number;
    discountCode?: IDiscount | null; // Discount
    discount?: IDiscount | null; // Discount
    total: number;
    planQuantityDiscountPerItem: number;
    planPostDiscountPerItem: number;
  }
}

export const EXTRA_PRODUCT = {
  HEALTH_SCOOP_LITTER: "Scoop Health",
  PLANET_SCOOP_LITTER: "Scoop Planet",
};
export interface ILitterOptionsResponse {
  type: AddOnPlanType;
  options: ILitterPlanSignUpOption[];
}

export interface Cat {
  name: string;
  catId: string;
}

export interface Cats {
  cat: Cat;
}

export interface Customer {
  cats: { [ key: string ]: Cats };
  customerId: string;
  rechargeCustomerId: string;
  addressId: string;
}
export interface ILitterSignUpPreference {
  quantity?: string;
  discountCode?: string;
  deliveryDate?: string; // Of the format "2022-07-14"
}

export interface IReactivateSubscriptionRequest {
  deliveryDate: string;
  catIDs: string[];
  mealPlan: MealPlan;
  reactivate: boolean;
}
export interface ILitterPlanSummaryRequest {
  quantity?: number;
  discountCode?: string;
  deliveryDate?: string; // Of the format "2022-07-14"
  cadence: number;
}

export interface ICreateLitterCustomer {
  firstName: string;
  lastName: string;
  email: string;
  postcode: string;
  cats: ICheckoutCat[];
  addonPlanType: AddOnPlanType;
  optInReason?: string | null;
  catCountInHousehold: number;
  fbc?: string;
  fbp?: string;
  formName?: string;
  customerId?: string;
}

export interface IBreederSignUpTrialistRequest {
  firstName: string;
  lastName: string;
  email: string;
  postcode: string;
  catData: GccfCatCheckoutDetails[];
  deliveryDate: string; // YYYY-MM-DD
  billingAddress: BillingAddress;
  shippingAddress: ShippingAddress;
  formUsed: string;
  catsInHousehold: number;
}

export interface IBreederSignUpTrialistResponse {
  katkinCustomerId: string;
  error?: string;
  message?:string;
}

export interface ICustomerExistsRequest {
  email: string;
}

export interface ICustomerExistsResponse {
  doesCustomerExist: boolean;
}

export interface ICreateCustomerResponse {
  customerId: string;
  catIds: { [ key: string ]: string }; // catName: catId for some reason...
  authData: TokenSuccessResponse;
  gtmData: unknown;
}

export type TrialType = "TWENTY_OFF" | "TWENTY_OFF_CROSS_SELL" | "GOOGLE_SHOPPING" | "NOT_A_TRIAL" | "HELLO_FRESH" | "TRY_5" |
"CL_5" |
"FOOD_5" |
"MF_5" |
"BD_5" |
"HG_5" |
"PACT_5" |
"G_5" |
"CO_5" |
"HOUSE_5" |
"NT_5" |
"COOK_5" |
"HF_5" |
"BAD_5";

export interface IFreshOptionsRequest {
  deliveryDate: string; // YYYY-MM-DD
  discountCode: string | undefined;
  trialType: TrialType;
  discountNibbles: TDiscountNibbles
  catData: CatRecipeDetails[];
  flowTrialType?: TrialType;
}

export interface IFreshOptionsNewCustomerRequest {
  deliveryDate: string; // YYYY-MM-DD
  discountCode: string | undefined;
  trialType: TrialType;
  discountNibbles: TDiscountNibbles
  catData: CatRecipeDetailsWithoutId[];
  numberOfCats?: number;
  flowTrialType?: TrialType;
}

export type CatRecipeDetailsWithoutId = Omit<CatRecipeDetails, "catId">;

export interface CatRecipeDetails {
  catId: string;
  catName: string;
  sprinklesQuantity?: number;
  nibblesSalmonQuantity?: number;
  nibblesChickenQuantity?: number;
  portion: 2;
  recipeTitleToQuantity: Record<string, number>;

  weight: string | undefined;
  bodyType: BodyType | undefined;
  lifestyle: Lifestyle;
  birthday: string | undefined; // YYYY-MM-DD
  allergyList: string[] | undefined;

  catLifeStage?: CatLifeStage;
  catAgeYears?: number;
  catAgeMonths?: number;
  catAgeWeeks?: number;
  catHasAllergies?: boolean;
  catPlanRemoved?: boolean;
}

export type MealPlan =
  | "Plan28x28"
  | "Plan28x56"
  | "Plan14x14"
  | "PlanTrial14x14"
  | "Plan7x7"
  | "Plan42x42";

export interface IExistingFreshCheckoutRequest {
  catData: CatCheckoutDetailsExistingCustomer[];
  mealPlan: MealPlan;
  deliveryDate: string; // YYYY-MM-DD
  discountCode: string | undefined;
  chargeImmediately: boolean;
  formUsed: string;
  flowTrialType?: TrialType;
}

export interface CatCheckoutDetailsExistingCustomer extends Omit<CatCheckoutDetails, "catName"> {
  name: string;
}

export interface INewFreshCheckoutRequest {
  deliveryDate: string; // YYYY-MM-DD
  email: string;
  customerId: string;
  discountCode: string;
  catData: CatCheckoutDetails[];
  catsInHousehold: number;
  formUsed: string;
  trialType: TrialType;
  postcode: string;
  billingAddress?: BillingAddress;
  shippingAddress?: ShippingAddress;
  fbclid?: string;
  flowTrialType?: TrialType;
  discountNibbles?: string;
}

export interface INewFreshCheckoutWithoutAddressRequest extends
  INewFreshCheckoutWithoutShippingAddressRequest, INewFreshCheckoutWithoutBillingAddressRequest {
  address: AddressValue;
  firstName: string;
  lastName: string;
}

type INewFreshCheckoutWithoutShippingAddressRequest = Omit<INewFreshCheckoutRequest, "shippingAddress">;
export type INewFreshCheckoutWithoutBillingAddressRequest = Omit<INewFreshCheckoutRequest, "billingAddress">;

export interface IGccfFreshCheckoutWithoutAddressRequest extends
  IGccfFreshCheckoutWithoutShippingAddressRequest, IGccfFreshCheckoutWithoutBillingAddressRequest {
  address: AddressValue;
  firstName: string;
  lastName: string;
}

type IGccfFreshCheckoutWithoutShippingAddressRequest = Omit<IBreederSignUpTrialistRequest, "shippingAddress">;
type IGccfFreshCheckoutWithoutBillingAddressRequest = Omit<IBreederSignUpTrialistRequest, "billingAddress">;

type TFreshBelief = 1 | 2 | 3 | 4;

export interface ICreateFreshCustomer {
  firstName: string;
  lastName: string;
  email: string;
  postcode?: string;
  cats: CatCreationFresh[];
  optInReason?: string | null;
  catCountInHousehold: number;
  trialType: TrialType;
  fbclid?: string;
  fbp?: string;
  discountCode?: string;
  deliveryDate?: string; // YYYY-MM-DD
  billingAddress?: BillingAddress;
  shippingAddress?: ShippingAddress;
  customerId?: string;
  freshBelief?: TFreshBelief;
  catTreats?: string;
  switchReasons?: string[];
  flowTrialType?: TrialType;
}

export interface ICreateInfluencerCustomer extends ICreateFreshCustomer {
  cats: InfluencerCheckoutDetails[];
}

export interface CatCreationFresh {
  name: string;
  birthday: string; // YYYY-MM-DD
  bodyType: BodyType;
  brand: string | undefined;
  breed: Breed;
  benefits: string[] | undefined;
  feed: string | undefined;
  fussiness: string | undefined;
  gender: Gender;
  isBirthdayPrecise: boolean | undefined;
  isNeutered: boolean | undefined;
  lifestyle: Lifestyle;
  medicalConditions: string[] | undefined;
  medicalConditionsDetails: string | undefined;
  weight: string;
  id: string | undefined;
}

export interface GccfCatCheckoutDetails extends CatCreationFresh {
  kittenGccfNumber: number;
  recipeTitleToQuantity: Record<string, number>;
}

export interface InfluencerCheckoutDetails extends CatCreationFresh {
  recipeTitleToQuantity: Record<string, number>;
}

export interface CatCheckoutDetails {
  catName: string;
  birthday: string; // YYYY-MM-DD
  bodyType: BodyType;
  brand: string | undefined;
  breed: Breed;
  benefits: string[] | undefined;
  feedList: string[] | undefined;
  fussiness: string | undefined;
  gender: Gender;
  isBirthdayPrecise: boolean | undefined;
  isNeutered: boolean | undefined;
  lifestyle: Lifestyle;
  medicalConditions: string[] | undefined;
  allergyList: string[] | undefined;
  medicalNotes: string | undefined;
  weight: string;
  id: string | undefined;
  nibblesChickenQuantity?: number,
  nibblesSalmonQuantity?: number,
  sprinklesQuantity?: number,
  recipeTitleToQuantity: Record<string, number>

  catLifeStage?: CatLifeStage;
  catAgeYears?: number;
  catAgeMonths?: number;
  catAgeWeeks?: number;
  catHasAllergies?: boolean;
  catHasMedicalConditions?: boolean;
  catPlanRemoved?: boolean;
}

export interface IFreshOptionsResponse {
  catIdToData: Record<string, CatSubscription>;
  deliveryDate: string;
  totalSubscription: SubscriptionDetail;
  totalSubscriptionWithDiscount?: SubscriptionDetail;
  totalTrialDiscount: SubscriptionDetail;
  totalTrialNoDiscount: SubscriptionDetail;
  totalTrialNormal: SubscriptionDetail;
  discount?: DiscountDetail;
}

export interface DiscountDetail {
  code: string;
  discount_type: string;
  value: number;
  duration: string;
  duration_usage_limit: string;
}

interface CatSubscription {
  catId: string;
  catName: string;
  mealPlanSize: MealPlanSize;
  subscription: SubscriptionDetail;
  subscriptionWithDiscount?: SubscriptionDetail;
  trialDiscount: SubscriptionDetail;
  trialNoDiscount: SubscriptionDetail;
  trialNormal: SubscriptionDetail;

}

type Extra = "Sprinkles" | "Nibbles Chicken" | "Nibbles Salmon";
export type RecipeProductTitle = "BAAA" | "CLUCK" | "MOOO" | "SPLASH" | "GOBBLE" | "QUACK" | "OINK";
export type ExtraProductTitle = "SPRINKLES" | "NIBBLES_CHICKEN" | "NIBBLES_SALMON";

interface BaseDetail<K, V> {
  price: number;
  pricePerItem: number;
  quantity: number;
  title: K;
  displayName: K;
  productTitle: V;
}

type RecipeDetail = BaseDetail<Recipe, RecipeProductTitle>;
type ExtraDetail = BaseDetail<Extra, ExtraProductTitle>;

export interface SubscriptionDetail {
  days: number;
  deliveryPrice: number;
  deliveryPricePerDay: number;
  extrasPerTitle: { [ key: string ]: ExtraDetail };
  extrasPricePerDay: number;
  freshMealPlanPrice: number;
  freshMealPlanPricePerDay: number;
  freshMealPlanPricePerPortion: number;
  portion: number;
  priceOfExtras: number;
  recipesPerTitle: { [ key: string ]: RecipeDetail };
  totalExtras: number;
  totalPrice: number;
  totalPricePerDay: number;
  totalPricePerPortion: number;
  totalRecipes: number;
}

export interface ICancelSubscriptionRequest {
  rechargeSubscriptionId: number;
  reason: CancellationReasonData;
  survey: SurveyResult;
}

export interface IUpdateFreshMealPlanDeliveryDateRequest {
  freshMealPlanId: string;
  freshDeliveryDate: string;
}

interface NpsCategoryScoring {
  quality?: boolean;
  companyMission?: boolean;
  customerService?: boolean;
  subscriptionService?: boolean;
  packaging?: boolean;
  eatsTheFood?: boolean;
  cost?: boolean;
  healthBenefits?: boolean;
}

interface SurveyResult {
  score?: number;
  reason?: string;
  improvements?: string;
  categoryScoring?: NpsCategoryScoring;
  postKatKinFood?: string;
}

interface CancellationReasonData {
  primaryReason: CancellationReason;
  secondaryReason?: CancellationReason;
  freeText?: string;
}

export interface UserId {
  authUserId?: string;
  anonymousId?: string;
  accessToken?: string;
  firstName?: string;
  lastName?: string;
}
